import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { MSkeleton } from '@Components/Base';
import { FormikHelpers, useFormik } from 'formik';
import {
  ApprovalHistory,
  RequestListSearchResponse,
} from '@Types/api/requestList';
import { PresentationContainer, ViewResolver } from '@Components/index';

import {
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
} from 'react-router-dom';
import {
  useAppDispatch,
  useAppSelector,
  useIsLoadedConent,
  useRequestRefetcher,
  useStatusFinderByNum,
} from '@Hooks/index';

import { MaterialRequisitionFormResponse } from '@Types/api/form';

import { RequestList } from '@Components/RequestList';
import { modalActions } from '@Store/slices/modalSlice';
import { getModalStatus } from '@Store/selectors/modalSelector';
import { ViewWidget } from '@Components/ViewWidget';
import { parseSelectedFiltersToOptions } from '@Features/utils/parseSelectedFiltersToOptions';
import { filterOptionsActions } from '@Store/slices/filterOptionsSlice';
import { SortConfig } from '@Features/types/SortConfig';
import { FiltersWidget } from '@Components/FiltersWidget';
import { ApprovalHistory as ApprovalHistoryModal } from '@Components/ApprovalHistory';
import { SearchCriteria } from '@Types/api/common/filters';
import { prepareSearchCriterias } from '@Features/utils/prepareSearchCriterias';
import { NotificationContext } from '@Context/NotificationContext';
import { TABLE_PAGE_DEFAULT } from '@Features/constants';
import {
  getCompaniesDictSelector,
  getCostCenterTypeDict,
  getCurrencies,
  getMrForDict,
  getRequisitionStatusesDictSelector,
  getWarrantyTypeDict,
} from '@Store/selectors/dictionarySelector';
import { TypeDict } from '@Types/index';
import { isFiltersEmptySelector } from '@Store/selectors/filterOptionsSelector';
import { eventMessageActions } from '@Store/slices/eventMessageSlice';

import { useGetUsersListByNameQuery } from '@Store/api/userApiSlice';
import {
  useGetCostCenterMutation,
  useGetEmpNumberByUserIdFnMutation,
} from '@Store/api/dictionaryApiSlice';
import { currentUserSelector } from '@Store/selectors/userInfoSelector';
import {
  useCancelRequisitionRequestMutation,
  useGetApprovalHistoryMutation,
  usePostCopyRequisitionMutation,
  useSubmitRequestMutation,
} from './store/api/MaterialRequestApiSlice';
import { ColumnDataType } from './list/type';
import { dataSourceMap } from './list/DataSource';
import { mapColumns } from './list/MapColumns';
import Filters from './search/Filter';
import {
  filterTypes,
  getFiltersMapper,
  initialValuesFilterForm,
} from './search/filters.utils';
import { FilterForm } from './search/types';
import RequestViewMobileForm from './form/View/mobile/RequestViewMobileForm';
import RequestViewForm from './form/View/RequestViewForm';
import { findCostCenterType } from '../utils/finder';
import { materialRequestActions } from './store/materialRequestSlice';
import { CopyRequestModal } from '../components/CopyRequestModal';
import { materialCostCenters } from './store/selectors/materialRequestSelector';

type handleApplyFilterType = (
  values: FilterForm,
  formikHelpers: FormikHelpers<FilterForm>
) => void;

interface CreatedEmpNoInfo {
  id: number;
  ccTypeId: number;
  ccId: number;
  compId: number;
  empNo: string;
}

type PromisesEmpNo = Array<Promise<CreatedEmpNoInfo>>;

const createdUsers = new Map();

const MaterialRequisition: React.FC = () => {
  const {
    cancelSuccessNotification,
    errorNotification,
    submitSuccessNotification,
    successNotification,
  } = useContext(NotificationContext);
  const { isRequestFormPage } = useOutletContext<{
    isRequestFormPage: boolean;
  }>();

  const isRequestFormPagePrev = useRef(isRequestFormPage);
  const currentMaterialCostCenters = useAppSelector(materialCostCenters);
  const isFiltersEmpty = useAppSelector(isFiltersEmptySelector);
  const isViewWidgetOpen = useAppSelector(getModalStatus);
  const statuses = useAppSelector(getRequisitionStatusesDictSelector);
  const costCenterTypes = useAppSelector(getCostCenterTypeDict);
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [getApprovalHistory] = useGetApprovalHistoryMutation();
  const [postCopyRequisition] = usePostCopyRequisitionMutation();
  const [getCostCenter] = useGetCostCenterMutation();
  const [cancelRequest] = useCancelRequisitionRequestMutation();
  const [submitRequest] = useSubmitRequestMutation();
  const currentUser = useAppSelector(currentUserSelector);
  const [tagsValues, setTagsValues] = useState(initialValuesFilterForm);
  const [showTags, setShowTags] = useState(false);
  const [showFiltersError, setShowFiltersError] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [searchValue, setSearchValue] = useState<string | undefined>(undefined);
  const [sorting, setSorting] = useState<SortConfig | null>(null);
  const [filters, setFilters] = useState<SearchCriteria[] | undefined>(
    undefined
  );
  const [dataSource, setDataSource] = useState<Array<ColumnDataType>>([]);
  const [listItems, setListItems] = useState<
    {
      id: number;
      ccId: number;
      compId: number;
      ccTypeId: number;
      createdBy: number;
    }[]
  >([]);

  const [createdEmpNumbers, setCreatedEmpNumbers] = useState<
    {
      id: number;
      ccTypeId: number;
      ccId: number;
      compId: number;
      empNo: string;
    }[]
  >([]);
  const [isApprovalHistoryOpen, setIsApprovalHistoryOpen] = useState(false);
  const [isCopyRequestOpen, setIsCopyRequestOpen] = useState(false);
  const [approvalHistoryData, setApprovalHistoryData] = useState<
    ApprovalHistory[] | null
  >(null);
  const [activeRequestId, setActiveRequestId] = useState('');
  const [activeRequestNo, setActiveRequestNo] = useState('');
  const { data: users = [] } = useGetUsersListByNameQuery(null);
  const [getEmpNumber] = useGetEmpNumberByUserIdFnMutation();
  const companies = useAppSelector(getCompaniesDictSelector);
  const currencies = useAppSelector(getCurrencies);
  const mrForTypes = useAppSelector(getMrForDict);
  const warranties = useAppSelector(getWarrantyTypeDict);

  const closeFilters = (): void => {
    setIsFiltersOpen(false);
  };

  const openFilters = (): void => {
    setIsFiltersOpen(true);
  };

  const handleApplyFilters: handleApplyFilterType = (options): void => {
    const filtersMapper = getFiltersMapper(
      statuses,
      costCenterTypes,
      companies,
      currencies,
      mrForTypes,
      warranties,
      currentMaterialCostCenters
    );
    const searchCriterias = prepareSearchCriterias(
      options,
      filterTypes,
      filtersMapper
    );
    setFilters(searchCriterias);

    const parsedOptions = parseSelectedFiltersToOptions(options);

    // true if filters was empty and empty after submit
    const isFiltersStillEmpty = isFiltersEmpty && !parsedOptions.length;
    setShowFiltersError(isFiltersStillEmpty);

    if (!isFiltersStillEmpty) {
      closeFilters();
    }

    dispatch(filterOptionsActions.setFilterSettings(searchCriterias));
    dispatch(filterOptionsActions.setFilterOptions(parsedOptions));
    dispatch(eventMessageActions.sendEventType('FILTER'));

    setShowTags(true);
    setTagsValues(options);
  };

  const formik = useFormik({
    initialValues: initialValuesFilterForm,
    enableReinitialize: true,
    onSubmit: handleApplyFilters,
  });

  const clearParams = (): void => {
    setSorting(null);
    setFilters(undefined);
    setSearchValue(undefined);
    setTagsValues(initialValuesFilterForm);
    formik.setValues(initialValuesFilterForm);
  };

  const {
    currentPage,
    fetchRequest,
    handleLoadMore,
    onChangeCurrentPage,
    isSuccess,
    listRequests,
    requestsDataList,
  } = useRequestRefetcher({
    clearParams,
    filters,
    isRequestFormPage,
    isRequestFormPagePrev,
    requestType: 'Material',
    searchValue,
    sorting,
  });
  const isContentLoaded = useIsLoadedConent(isSuccess);
  const location = useLocation();

  const isRedirected = location.state?.redirected;

  const { findStatus } = useStatusFinderByNum('Material');

  useEffect(() => {
    isRequestFormPagePrev.current = isRequestFormPage;
  }, [isRequestFormPage]);

  const handleSort = useCallback(
    (sortConfig: SortConfig | null) => {
      fetchRequest(TABLE_PAGE_DEFAULT, sortConfig);

      onChangeCurrentPage(TABLE_PAGE_DEFAULT);
      setSorting(sortConfig);
    },
    [fetchRequest]
  );

  const handleSubmitRequest = useCallback(
    (requisitionId: string): void => {
      submitRequest(String(requisitionId))
        .unwrap()
        .then(() => {
          submitSuccessNotification(String(requisitionId));
          fetchRequest();
        })
        .catch(({ data }) => {
          errorNotification(data);
        });
    },
    [fetchRequest]
  );

  const handleApprovalHistory = useCallback((requestNo: string) => {
    setActiveRequestId(requestNo);

    getApprovalHistory(requestNo)
      .unwrap()
      .then((approvalHistoryResp) => {
        if (Array.isArray(approvalHistoryResp)) {
          setIsApprovalHistoryOpen(true);
          setApprovalHistoryData(approvalHistoryResp);
        }
      })
      .catch(({ data }) => {
        errorNotification(data);
      });
  }, []);

  const findCostCenterTypeItem = (ccTypeId: number): TypeDict | undefined =>
    findCostCenterType(costCenterTypes, ccTypeId);

  const getCostCenterItems = useCallback(
    (
      allocationId: string | number,
      compId: string | number,
      empNo: string
    ): Promise<void> => {
      return getCostCenter({
        allocationId,
        compId,
        empNo,
      })
        .unwrap()
        .then((response) => {
          dispatch(materialRequestActions.setMaterialCostCenters(response));
        });
    },
    [currentUser.empNumber]
  );

  useLayoutEffect(() => {
    const requests =
      listRequests as RequestListSearchResponse<MaterialRequisitionFormResponse>;
    if (requests?.content) {
      const items = requests?.content.map((listItem) => ({
        id: listItem.requisitionId,
        ccId: listItem.ccId,
        compId: listItem.compId,
        ccTypeId: listItem.ccTypeId,
        createdBy: listItem.createdBy,
      }));
      setListItems(items);
    }
  }, [listRequests]);

  useLayoutEffect(() => {
    if (listItems.length) {
      const promisesEmpNo: PromisesEmpNo = [];

      if (isRedirected) {
        createdUsers.clear();
      }

      listItems.forEach((item) => {
        const createdUserRecord = createdUsers.get(
          `${item.createdBy}-${item.ccTypeId}-${item.compId}`
        );

        if (!createdUserRecord) {
          createdUsers.set(
            `${item.createdBy}-${item.ccTypeId}-${item.compId}`,
            item.createdBy
          );

          promisesEmpNo.push(
            getEmpNumber(item.createdBy)
              .unwrap()
              .then((response) => {
                return {
                  id: item.id,
                  compId: item.compId,
                  ccTypeId: item.ccTypeId,
                  ccId: item.ccId,
                  empNo: response,
                };
              })
          );
        }
      });

      Promise.all(promisesEmpNo).then((values) => {
        setCreatedEmpNumbers(values);
      });
    }
  }, [listItems, isRedirected]);

  useLayoutEffect(() => {
    if (createdEmpNumbers.length && createdEmpNumbers[0]) {
      createdEmpNumbers.forEach((item) => {
        getCostCenterItems(item.ccTypeId, item.compId, item.empNo);
      });
      // hard reset for call endpoint once
      createdEmpNumbers.length = 0;
    }
  }, [createdEmpNumbers]);

  useEffect(() => {
    const requests =
      listRequests as RequestListSearchResponse<MaterialRequisitionFormResponse>;

    if (currentMaterialCostCenters.length && requests?.content) {
      const changedList = requests?.content.map((request) => {
        const item = currentMaterialCostCenters.find(
          (costCenter) => costCenter.id === request.ccId
        );
        return {
          ...request,
          costCenterName: item?.name,
        } as MaterialRequisitionFormResponse;
      });
      setDataSource(
        dataSourceMap(changedList, findStatus, findCostCenterTypeItem)
      );
    }
  }, [currentMaterialCostCenters, listRequests]);

  const handleChangePage = useCallback((page: number): void => {
    onChangeCurrentPage(page);
    dispatch(eventMessageActions.sendEventType('REFETCH'));
  }, []);

  useEffect(() => {
    dispatch(filterOptionsActions.setFilterOptions(null));
  }, []);

  const handleEditRecord = useCallback(
    (recordKey: string): void => {
      navigate(`${pathname}/edit/${recordKey}`);
    },
    [pathname]
  );

  const handleViewRecord = useCallback((recordKey: string): void => {
    dispatch(
      modalActions.setStatusModal({
        status: true,
        key: String(recordKey),
      })
    );
  }, []);

  const handleOpenCopyRequest = useCallback(
    (recordKey: string, mrNo: string): void => {
      setActiveRequestId(recordKey);
      setActiveRequestNo(mrNo);
      setIsCopyRequestOpen(true);
    },
    []
  );

  const handleCopyRequest = useCallback(
    (isMaterials: boolean, isAttachments: boolean): void => {
      postCopyRequisition({
        mrNo: activeRequestNo,
        isFileAttached: isAttachments,
        isMatAdd: isMaterials,
        headerDesc: '',
      })
        .unwrap()
        .then(() => {
          successNotification(
            `Request No. ${activeRequestId} has been successfully copied!`
          );
          fetchRequest();
          setIsCopyRequestOpen(false);
        })
        .catch(({ data }) => {
          errorNotification(data);
        });
    },
    [postCopyRequisition, activeRequestId, activeRequestNo]
  );

  const handleCloseViewWidget = (): void => {
    dispatch(modalActions.setStatusModal({ status: false, key: '' }));
  };

  const handleSearch = useCallback((value: string | null) => {
    setSearchValue(value || undefined);
    dispatch(eventMessageActions.sendEventType('SEARCH'));
  }, []);

  const tagsFormik = {
    ...formik,
    values: tagsValues,
  };

  const handleCancelRequest = useCallback(
    (requestNo: string) => {
      const reqId = String(requestNo);

      cancelRequest(reqId)
        .unwrap()
        .then(() => {
          cancelSuccessNotification(reqId);

          fetchRequest();
        })
        .catch(({ data }) => {
          errorNotification(data);
        });

      handleCloseViewWidget();
    },
    [fetchRequest]
  );

  // reset dataSource when content is null
  useEffect(() => {
    if (!listRequests?.content) {
      setDataSource([]);
    }
  }, [listRequests]);

  return (
    <PresentationContainer>
      {isRequestFormPage ? (
        <Outlet />
      ) : (
        <>
          <FiltersWidget
            onSearchSubmit={handleSearch}
            isOpen={isFiltersOpen}
            onClose={closeFilters}
            onOpen={openFilters}
            formik={tagsFormik}
            showTags={showTags}
          >
            <Filters showError={showFiltersError} formik={formik} />
          </FiltersWidget>
          {(isContentLoaded && dataSource.length) ||
          listRequests?.totalElements === 0 ? (
            <RequestList
              onLoadMore={handleLoadMore}
              onEdit={handleEditRecord}
              onView={handleViewRecord}
              onApprovalHistory={handleApprovalHistory}
              onSubmit={handleSubmitRequest}
              onCancel={handleCancelRequest}
              onChangePage={handleChangePage}
              onCopy={handleOpenCopyRequest}
              currentPage={currentPage}
              onSort={handleSort}
              listRequests={
                listRequests as RequestListSearchResponse<MaterialRequisitionFormResponse>
              }
              requestsDataList={
                requestsDataList as Array<MaterialRequisitionFormResponse>
              }
              mapColumns={mapColumns}
              dataSource={dataSource}
              requestType="Material"
              isFiltersApplied={Boolean(filters)}
              isSearchApplied={Boolean(searchValue)}
            />
          ) : (
            <MSkeleton />
          )}

          <ViewWidget isOpen={isViewWidgetOpen} onClose={handleCloseViewWidget}>
            <ViewResolver
              desktop={<RequestViewForm />}
              mobile={
                <RequestViewMobileForm
                  onCancel={handleCancelRequest}
                  onEdit={handleEditRecord}
                  onSubmit={handleSubmitRequest}
                />
              }
            />
          </ViewWidget>
          <CopyRequestModal
            id={activeRequestNo}
            isOpen={isCopyRequestOpen}
            onCancel={() => setIsCopyRequestOpen(false)}
            onSuccess={handleCopyRequest}
          />
          <ApprovalHistoryModal
            isOpen={isApprovalHistoryOpen}
            onClose={() => {
              setIsApprovalHistoryOpen(false);
            }}
            data={approvalHistoryData}
            requestNo={activeRequestId}
            users={users}
          />
        </>
      )}
    </PresentationContainer>
  );
};

export default MaterialRequisition;
